import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Button,
  HStack,
  Icon,
  Text,
  Input,
  InputGroup,
  InputLeftElement,
  Center,
  Spinner,
} from "@chakra-ui/react";
import { FaPlus } from "react-icons/fa";
import { SearchIcon } from "@chakra-ui/icons";
import { useNavigate } from "react-router-dom";
import JobList from "./JobList";
import JobViewComponent from "./JobViewComponent";
import { Header } from "build-ts/metadata";
import { listScheduledJobs } from "services/ScheduleServices";

interface AnalystJobsComponentProps {
  analystId: string | undefined;
  setIsViewingOrEditingJob?: (value: boolean) => void;
  analystJobsMap: Map<string, Header[]>;
  setAnalystJobsMap: React.Dispatch<React.SetStateAction<Map<string, Header[]>>>;
  initialJobId?: string;
}

const AnalystJobsComponent: React.FC<AnalystJobsComponentProps> = ({
  analystId,
  setIsViewingOrEditingJob,
  analystJobsMap,
  setAnalystJobsMap,
  initialJobId,
}) => {
  const navigate = useNavigate();
  const [isCreatingOrViewingJob, setIsCreatingOrViewingJob] = useState<boolean>(false);
  // Start in loading state immediately when deep-linking to a specific job
  const [isLoading, setIsLoading] = useState<boolean>(!!initialJobId);
  const [jobs, setJobs] = useState<Header[]>([]);
  const [selectedJob, setSelectedJob] = useState<Header | undefined>(undefined);
  const [searchTerm, setSearchTerm] = useState<string>("");

  // Filter jobs based on search term
  const filteredJobs = useMemo(() => {
    if (!searchTerm.trim()) {
      return jobs;
    }

    const lowerSearchTerm = searchTerm.toLowerCase();
    return jobs.filter((job) => {
      return job.name?.toLowerCase().includes(lowerSearchTerm);
    });
  }, [jobs, searchTerm]);

  const getJobs = async (analystId: string) => {
    setIsLoading(true);
    if (analystId) {
      const response = await listScheduledJobs(
        "SCENARIO_JOB",
        -1,
        1,
        analystId,
        "LAST_MODIFIED",
        false,
        true, /* includeScheduleActivity */
        "", /* activeSchedule */
      );
      if (response) {
        setJobs(response);
        // Update the map with the new jobs list
        setAnalystJobsMap(prevMap => {
          const newMap = new Map(prevMap);
          newMap.set(analystId, response);
          return newMap;
        });
      }
    } else {
      setJobs([]);
    }
    setIsLoading(false);
  };

  // Initialize jobs from the map, or fetch directly if the map doesn't have them yet
  useEffect(() => {
    if (!analystId) return;
    if (analystJobsMap.has(analystId)) {
      const jobsFromMap = analystJobsMap.get(analystId);
      setJobs(jobsFromMap || []);
    } else {
      // Map not populated yet (e.g. arrived via deep-link before background fetch finished)
      getJobs(analystId);
    }
  }, [analystId, analystJobsMap]);

  // Auto-open a specific job when arriving via deep-link (/jobs/:jobId)
  useEffect(() => {
    if (!initialJobId || jobs.length === 0 || isCreatingOrViewingJob) return;
    const job = jobs.find(j => j.id === initialJobId);
    if (job) {
      handleJobClick(job);
    }
  }, [jobs, initialJobId]);

  const handleCreateNewJob = () => {
    setSelectedJob(undefined);
    setIsCreatingOrViewingJob(true);
    setIsViewingOrEditingJob?.(true);
    if (analystId) {
      navigate(`/analyst/${analystId}/jobs`, { replace: true });
    }
  };

  const handleJobClick = (job: Header) => {
    setSelectedJob(job);
    setIsCreatingOrViewingJob(true);
    setIsViewingOrEditingJob?.(true);
    if (analystId && job.id) {
      navigate(`/analyst/${analystId}/jobs/${job.id}`, { replace: true });
    }
  };

  const handleBackToList = () => {
    setIsCreatingOrViewingJob(false);
    setSelectedJob(undefined);
    setIsViewingOrEditingJob?.(false);
    if (analystId) {
      navigate(`/analyst/${analystId}/jobs`, { replace: true });
      getJobs(analystId);
    }
  };

  const handleRefresh = () => {
    if (analystId) {
      getJobs(analystId);
    }
  };

  if (isCreatingOrViewingJob) {
    return (
      <JobViewComponent
        onBack={handleBackToList}
        isCreatingNew={!selectedJob}
        selectedJob={selectedJob}
      />
    );
  }

  // While waiting to open a deep-linked job, show a full-page spinner instead of the list
  if (initialJobId && isLoading) {
    return (
      <Center height="100%">
        <Spinner size="xl" color="purple.600" thickness="3px" />
      </Center>
    );
  }

  return (
    <Box height="100%" bg="white" p={4}>
      {/* Header with Job Count, Search Bar, and Create Button */}
      <HStack justify="space-between" align="center" mb={1} spacing={4}>
        {/* Job Count */}
        <Text fontSize="sm" fontWeight="500" color="gray.400" m={0} minW="150px">
          {/* Showing {filteredJobs.length} {filteredJobs.length === 1 ? "Analysis" : "Analyses"} */}
        </Text>

        {/* Search Bar */}
        <InputGroup size="md" maxW="500px" flex="1">
          <InputLeftElement pointerEvents="none" height="full">
            <SearchIcon color="gray.400" boxSize={4} />
          </InputLeftElement>
          <Input
            placeholder="Search analyses by name..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            bg="gray.50"
            border="1px"
            borderColor="gray.200"
            fontSize="sm"
            _hover={{
              borderColor: "gray.300",
              bg: "white"
            }}
            _focus={{
              borderColor: "purple.400",
              boxShadow: "0 0 0 1px var(--chakra-colors-purple-400)",
              bg: "white"
            }}
            _placeholder={{
              color: "gray.400"
            }}
          />
        </InputGroup>

        {/* Create New Job Button */}
        <Button
          variant="outline"
          size="sm"
          fontWeight="700"
          colorScheme="purple"
          leftIcon={<Icon as={FaPlus} boxSize={4} />}
          onClick={handleCreateNewJob}
        >
          New Analysis
        </Button>
      </HStack>

      {/* Job List */}
      <JobList
        jobs={filteredJobs}
        isLoadingJobs={isLoading}
        onJobClick={handleJobClick}
        onRefresh={handleRefresh}
      />
    </Box>
  );
};

export default AnalystJobsComponent;

